import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Briefcase, Activity, AlertCircle, CheckCircle2, AlertTriangle, Clock,
  ArrowUpDown, Upload, FileText, RefreshCcw, Shield, ChevronRight, Search
} from 'lucide-react';
import { getTenantToday } from '../utils/dateUtils';

import { DateField } from './shared/DateField';

/* ────────── helpers ────────── */
const fmt = (v: number | null | undefined) =>
  v == null ? '—' : `$${Math.abs(v) >= 1_000_000 ? (v / 1_000_000).toFixed(1) + 'M' : v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDelta = (v: number | null | undefined) => {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const s = abs >= 1_000_000 ? `$${(abs / 1_000_000).toFixed(2)}M` : `$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return v >= 0 ? `+${s}` : `-${s}`;
};
const cn = (...classes: (string | false | null | undefined)[]) => classes.filter(Boolean).join(' ');

/* ────────── types ────────── */
interface ReconRow {
  positionId: number;
  portfolioName: string;
  securityName: string;
  securityType: string;
  assetCategory: string;
  strategy: string;
  asOfDate: string;
  status: string;
  navReconciliation: { iborValue: number | null; adminReportedValue: number | null; navDifference: number | null; navDifferencePct: number | null };
  unitReconciliation: { iborUnits: number | null; adminReportedUnits: number | null; unitDifference: number | null };
  exceptions: string[];
}

interface CustodianFile {
  id: number;
  fileName: string;
  custodian: string;
  fileDate: string;
  uploadTime: string;
  uploadedBy: string;
  recordCount: number;
  status: string;
  fileType?: string;
}

interface ReconRun {
  id: number;
  fileId: number;
  runTime: string;
  runBy: string;
  status: string;
  totalCustodianPositions: number;
  totalOmsPositions: number;
  matchedCount: number;
  breakCount: number;
  completionTime: string;
}

interface ReconBreak {
  id: number;
  breakType: string;
  custodianCusip: string;
  omsCusip: string;
  custodianQuantity: number;
  custodianValue: number;
  custodianAccount: string;
  omsQuantity: number;
  omsValue: number;
  omsPortfolio: string;
  quantityDiff: number;
  valueDiff: number;
  status: string;
  resolutionNotes: string;
  // V331: engine's asset-class-aware first-pass hypothesis. Distinct from
  // operator-typed resolutionNotes; both surface side-by-side. Confidence
  // (HIGH | MED | LOW) drives the badge color.
  inferredReason?: string | null;
  inferredAction?: string | null;
  inferredConfidence?: 'HIGH' | 'MED' | 'LOW' | string | null;
}

type TabKey = 'dashboard' | 'upload' | 'files' | 'breaks';
type SortKey = 'securityName' | 'assetCategory' | 'status' | 'navDiff';

const FUND_ADMINS = [
  { code: 'AZTEC_GROUP', name: 'Aztec Group' },
  { code: 'CITCO', name: 'Citco Fund Services' },
  { code: 'SSC_GLS', name: 'SS&C GlobeOp' },
  { code: 'APEX', name: 'Apex Group' },
  { code: 'ALTER_DOMUS', name: 'Alter Domus' },
  { code: 'OTHER', name: 'Other Fund Admin' },
];

/* ────────── sub-components ────────── */
const statusBadge = (s: string) => {
  if (s === 'RECONCILED' || s === 'RESOLVED') return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
  if (s === 'BREAK' || s === 'OPEN')          return 'bg-red-500/10 text-red-400 border-red-500/20';
  if (s === 'INVESTIGATING')                   return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
  return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
};
const statusIcon = (s: string) => {
  if (s === 'RECONCILED' || s === 'RESOLVED') return <CheckCircle2 className="w-3 h-3" />;
  if (s === 'BREAK' || s === 'OPEN')          return <AlertTriangle className="w-3 h-3" />;
  return <Clock className="w-3 h-3" />;
};

const Kpi: React.FC<{ label: string; value: string | number; accent?: string; sub?: string; borderColor?: string }> = ({ label, value, accent = 'text-white', sub, borderColor }) => (
  <div className={cn("bg-slate-900/40 border border-white/5 rounded-xl p-5", borderColor && `border-l-4`)} style={borderColor ? { borderLeftColor: borderColor } : undefined}>
    <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-2">{label}</div>
    <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </div>
);

/* ════════════════════════════════════════════════════════════════ */
const PrivateAssetReconciliationScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabKey>('dashboard');

  /* ── dashboard / IBOR-vs-admin state ── */
  const [rows, setRows] = useState<ReconRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'RECONCILED' | 'BREAK' | 'PENDING'>('ALL');
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortAsc, setSortAsc] = useState(true);

  /* ── files / runs / breaks state ── */
  const [files, setFiles] = useState<CustodianFile[]>([]);
  const [runs, setRuns] = useState<ReconRun[]>([]);
  const [breaks, setBreaks] = useState<ReconBreak[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);

  /* ── upload state ── */
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [fileDate, setFileDate] = useState(getTenantToday());
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<{ success: boolean; message: string; recordCount?: number } | null>(null);
  const [connectEnabled, setConnectEnabled] = useState(false);

  /* ── resolve modal ── */
  const [resolveModal, setResolveModal] = useState<{ isOpen: boolean; breakId: number | null }>({ isOpen: false, breakId: null });
  const [resolveStatus, setResolveStatus] = useState('RESOLVED');
  const [resolveNotes, setResolveNotes] = useState('');

  /* ── data fetchers ── */
  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/reconciliation/private-assets');
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded — wait a moment.');
      else setError('Failed to load reconciliation data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFiles = useCallback(async () => {
    try {
      const res = await axios.get('/api/reconciliation/files');
      setFiles(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded.');
    }
  }, []);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await axios.get('/api/reconciliation/runs');
      setRuns(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded.');
    }
  }, []);

  const fetchBreaks = useCallback(async (runId?: number) => {
    try {
      const url = runId
        ? `/api/reconciliation/runs/${runId}/breaks`
        : '/api/reconciliation/breaks/open';
      const res = await axios.get(url);
      setBreaks(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      if (err.response?.status === 429) setError('Rate limit exceeded.');
    }
  }, []);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  useEffect(() => {
    let mounted = true;
    axios.get('/api/connect/status')
      .then(res => {
        if (mounted) setConnectEnabled(res.data?.enabled === true);
      })
      .catch(() => {
        if (mounted) setConnectEnabled(false);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (activeTab === 'files') { fetchFiles(); fetchRuns(); }
    else if (activeTab === 'breaks') { fetchBreaks(selectedRunId || undefined); }
  }, [activeTab, selectedRunId, fetchFiles, fetchRuns, fetchBreaks]);

  /* ── upload handler ── */
  const handleUpload = async () => {
    if (!selectedFile || !selectedAdmin) return;
    setIsUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      let res;
      if (selectedAdmin === 'AZTEC_GROUP' && connectEnabled) {
        formData.append('provider', 'AZTEC');
        formData.append('fileType', 'HOLDINGS');
        formData.append('businessDate', fileDate);
        formData.append('receivedVia', 'UI');
        res = await axios.post('/api/connect/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const manifestId = res.data?.manifest?.id;
        const recordCount = res.data?.custodianFile?.recordCount ?? res.data?.manifest?.rowCount;
        const runId = res.data?.reconciliationRun?.id;
        setUploadResult({
          success: true,
          message: manifestId
            ? `Registered in AxiomFlow Connect manifest #${manifestId} and reconciled`
            : 'Registered in AxiomFlow Connect',
          recordCount
        });
        setSelectedFile(null);
        fetchFiles();
        fetchRuns();
        if (runId) {
          setSelectedRunId(runId);
          setActiveTab('breaks');
          fetchBreaks(runId);
        }
        fetchResults();
      } else {
        formData.append('custodian', selectedAdmin);
        formData.append('fileDate', fileDate);
        res = await axios.post('/api/reconciliation/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (res.data.success) {
          setUploadResult({ success: true, message: 'File uploaded successfully!', recordCount: res.data.recordCount });
          setSelectedFile(null);
          fetchFiles();
          fetchResults();
        }
      }
    } catch (err: any) {
      setUploadResult({ success: false, message: err.response?.data?.error || 'Upload failed' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRunReconciliation = async (fileId: number) => {
    setLoading(true);
    try {
      const res = await axios.post(`/api/reconciliation/run/${fileId}`);
      if (res.data.success) { fetchFiles(); fetchRuns(); setSelectedRunId(res.data.runId); setActiveTab('breaks'); }
    } catch { /* swallow */ } finally { setLoading(false); }
  };

  const handleResolveBreak = async () => {
    if (!resolveModal.breakId) return;
    try {
      await axios.put(`/api/reconciliation/breaks/${resolveModal.breakId}/resolve`, { status: resolveStatus, notes: resolveNotes });
      setResolveModal({ isOpen: false, breakId: null });
      setResolveNotes('');
      fetchBreaks(selectedRunId || undefined);
      fetchResults();
    } catch { /* swallow */ }
  };

  /* ── derived stats ── */
  const total      = rows.length;
  const reconciled = rows.filter(r => r.status === 'RECONCILED').length;
  const breakCount = rows.filter(r => r.status === 'BREAK').length;
  const pending    = rows.filter(r => r.status === 'PENDING').length;
  const totalIbor  = rows.reduce((s, r) => s + (r.navReconciliation?.iborValue || 0), 0);
  const totalAdmin = rows.reduce((s, r) => s + (r.navReconciliation?.adminReportedValue || 0), 0);
  const totalDelta = totalIbor - totalAdmin;
  const healthPct  = total > 0 ? ((reconciled / total) * 100).toFixed(1) : '0.0';

  /* ── filter + sort ── */
  const filtered = filter === 'ALL' ? rows : rows.filter(r => r.status === filter);
  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'securityName')       cmp = a.securityName.localeCompare(b.securityName);
    else if (sortKey === 'assetCategory') cmp = a.assetCategory.localeCompare(b.assetCategory);
    else if (sortKey === 'status') {
      const order: Record<string, number> = { BREAK: 0, PENDING: 1, RECONCILED: 2 };
      cmp = (order[a.status] ?? 9) - (order[b.status] ?? 9);
    } else if (sortKey === 'navDiff') {
      const da = a.navReconciliation?.navDifference ?? 0;
      const db = b.navReconciliation?.navDifference ?? 0;
      cmp = Math.abs(db) - Math.abs(da);
    }
    return sortAsc ? cmp : -cmp;
  });

  const toggleSort = (key: SortKey) => { if (sortKey === key) setSortAsc(!sortAsc); else { setSortKey(key); setSortAsc(true); } };

  const SortTh: React.FC<{ k: SortKey; children: React.ReactNode; className?: string }> = ({ k, children, className }) => (
    <th className={cn("text-left text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4 cursor-pointer hover:text-slate-300 transition-colors", className)} onClick={() => toggleSort(k)}>
      <span className="flex items-center gap-1">{children}{sortKey === k && <ArrowUpDown className="w-3 h-3 text-blue-400" />}</span>
    </th>
  );

  /* ════════════════════════════════════════════════════════════════ */
  return (
    <div className="px-6 py-6 text-slate-100">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white tracking-tight flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-blue-400" />
            Private Asset Reconciliation
          </h2>
          <p className="text-slate-500 text-sm mt-1">IBOR vs Fund Admin — PE, Private Credit, RE Equity, RE Debt, ABF</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Tab bar */}
          <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
            {(['dashboard', 'upload', 'files', 'breaks'] as TabKey[]).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={cn(
                  "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize",
                  activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                )}
              >
                {tab}
              </button>
            ))}
          </div>
          <button onClick={() => { fetchResults(); fetchFiles(); fetchRuns(); fetchBreaks(); }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all">
            <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} /> Refresh
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-between gap-3 text-red-400 font-bold mb-6">
          <div className="flex items-center gap-3"><AlertCircle className="w-5 h-5" />{error}</div>
          <button onClick={() => { setError(null); fetchResults(); }} className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 transition-colors">Retry</button>
        </motion.div>
      )}

      {/* KPI strip — always visible */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Total Positions" value={total} sub="Across all alts" borderColor="#3b82f6" />
        <Kpi label="Reconciled" value={reconciled} accent="text-emerald-400" sub={`${healthPct}% match rate`} borderColor="#10b981" />
        <Kpi label="Breaks" value={breakCount} accent={breakCount > 0 ? 'text-red-400' : 'text-emerald-400'} sub="Require investigation" borderColor="#ef4444" />
        <Kpi label="Pending" value={pending} accent="text-amber-400" sub="Awaiting admin data" borderColor="#f59e0b" />
        <Kpi label="Admin Files" value={files.length || '—'} sub="Uploaded statements" borderColor="#8b5cf6" />
        <Kpi label="Health Score" value={`${healthPct}%`} accent={parseFloat(healthPct) >= 90 ? 'text-emerald-400' : 'text-amber-400'} sub="IBOR match rate" borderColor="#06b6d4" />
      </div>

      {/* ══════════════ DASHBOARD TAB ══════════════ */}
      {activeTab === 'dashboard' && (
        <div className="space-y-6">
          {/* IBOR-vs-Admin comparison matrix */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main table */}
            <div className="lg:col-span-2 bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-semibold text-white">IBOR vs Admin Reconciliation</span>
                  <span className="text-xs text-slate-500 ml-2">{filtered.length} positions</span>
                </div>
                {/* Filter chips */}
                <div className="flex items-center gap-1.5">
                  {(['ALL', 'BREAK', 'PENDING', 'RECONCILED'] as const).map(f => (
                    <button key={f} onClick={() => setFilter(f)}
                      className={cn(
                        "px-2.5 py-1 rounded-lg text-[10px] font-semibold uppercase tracking-wider transition-colors border",
                        filter === f ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-slate-800/50 text-slate-500 border-white/5 hover:text-slate-300'
                      )}>
                      {f === 'ALL' ? `All (${total})` : f === 'BREAK' ? `Breaks (${breakCount})` : f === 'PENDING' ? `Pending (${pending})` : `Matched (${reconciled})`}
                    </button>
                  ))}
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-20 text-slate-500 text-sm">Loading reconciliation data...</div>
              ) : sorted.length === 0 ? (
                <div className="flex items-center justify-center py-20 text-slate-500 text-sm">No positions to reconcile</div>
              ) : (
                <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
                      <tr className="border-b border-white/5">
                        <SortTh k="securityName">Position</SortTh>
                        <SortTh k="assetCategory">Category</SortTh>
                        <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">IBOR</th>
                        <th className="text-right text-[10px] font-semibold text-slate-500 uppercase tracking-widest py-3 px-4">Admin</th>
                        <SortTh k="navDiff" className="text-right">Delta</SortTh>
                        <SortTh k="status">Status</SortTh>
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map((r, i) => {
                        const diff = r.navReconciliation?.navDifference;
                        const pct  = r.navReconciliation?.navDifferencePct;
                        return (
                          <tr key={r.positionId ?? i} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                            <td className="py-2.5 px-4">
                              <div className="text-sm font-medium text-slate-200 truncate max-w-[220px]">{r.securityName}</div>
                              <div className="text-[10px] text-slate-500">{r.portfolioName}</div>
                            </td>
                            <td className="py-2.5 px-4"><span className="text-xs text-slate-400">{r.assetCategory}</span></td>
                            <td className="py-2.5 px-4 text-right font-mono text-sm text-slate-200">{fmt(r.navReconciliation?.iborValue)}</td>
                            <td className="py-2.5 px-4 text-right font-mono text-sm text-slate-200">
                              {r.navReconciliation?.adminReportedValue != null ? fmt(r.navReconciliation.adminReportedValue) : <span className="text-slate-600 italic text-xs">—</span>}
                            </td>
                            <td className="py-2.5 px-4 text-right font-mono text-sm">
                              {diff != null ? (
                                <span className={Math.abs(diff) < 0.01 ? 'text-emerald-400' : 'text-red-400'}>
                                  {fmtDelta(diff)}
                                  {pct != null && <span className="text-[10px] ml-1 opacity-60">({pct > 0 ? '+' : ''}{pct.toFixed(1)}%)</span>}
                                </span>
                              ) : <span className="text-slate-600">—</span>}
                            </td>
                            <td className="py-2.5 px-4">
                              <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border", statusBadge(r.status))}>
                                {statusIcon(r.status)}{r.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sidebar: Recent breaks + summary */}
            <div className="space-y-6">
              {/* Break summary */}
              <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
                <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  Open Break Summary
                </h4>
                <div className="space-y-3">
                  {rows.filter(r => r.status === 'BREAK').slice(0, 5).map((r, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-red-500/5 border border-red-500/10 rounded-lg">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium text-slate-200 truncate">{r.securityName}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{r.assetCategory} — {fmtDelta(r.navReconciliation?.navDifference)}</div>
                        {r.exceptions?.[0] && <div className="text-[10px] text-red-400/80 mt-1 truncate">{r.exceptions[0].split('—')[1]?.trim() || r.exceptions[0]}</div>}
                      </div>
                    </div>
                  ))}
                  {breakCount === 0 && <div className="text-xs text-slate-500 italic py-4 text-center">No open breaks</div>}
                </div>
                {breakCount > 0 && (
                  <button onClick={() => setActiveTab('breaks')} className="mt-4 w-full py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1">
                    View All Breaks <ChevronRight className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Net delta summary */}
              <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
                <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-400" />
                  Value Reconciliation
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400">IBOR Total</span>
                    <span className="font-bold text-white font-mono">{fmt(totalIbor)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400">Admin Total</span>
                    <span className="font-bold text-white font-mono">{fmt(totalAdmin)}</span>
                  </div>
                  <div className="border-t border-white/5 pt-2 flex justify-between items-center text-xs">
                    <span className="text-slate-400">Net Delta</span>
                    <span className={cn("font-bold font-mono", Math.abs(totalDelta) > 100 ? 'text-amber-400' : 'text-emerald-400')}>{fmtDelta(totalDelta)}</span>
                  </div>
                </div>
              </div>

              {/* Category breakdown */}
              <div className="bg-slate-900/40 border border-white/5 rounded-xl p-5">
                <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-blue-400" />
                  By Asset Class
                </h4>
                <div className="space-y-2">
                  {['PE/PC Fund', 'RE Equity', 'RE Debt', 'Fund/Structured'].map(cat => {
                    const catRows = rows.filter(r => r.assetCategory === cat);
                    const catBreaks = catRows.filter(r => r.status === 'BREAK').length;
                    if (catRows.length === 0) return null;
                    return (
                      <div key={cat} className="flex justify-between items-center text-xs">
                        <div className="flex items-center gap-2">
                          <div className={cn("w-2 h-2 rounded-full", catBreaks > 0 ? 'bg-red-400' : 'bg-emerald-400')} />
                          <span className="text-slate-400">{cat}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-slate-500">{catRows.length} pos</span>
                          {catBreaks > 0 && <span className="text-red-400 font-bold">{catBreaks} breaks</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ UPLOAD TAB ══════════════ */}
      {activeTab === 'upload' && (
        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-900/40 border border-white/5 rounded-xl p-8">
            <h3 className="text-lg font-bold text-white mb-2">Upload Fund Admin Statement</h3>
            <p className="text-xs text-slate-500 mb-6">NAV statements, loan tapes, valuation reports from your fund administrator</p>

            {/* Drop zone */}
            <div
              className={cn(
                "border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer",
                isDragging ? "border-blue-500 bg-blue-500/10" : "border-white/10 hover:border-blue-500/50 hover:bg-white/5"
              )}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={e => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files[0];
                if (file) { setSelectedFile(file); setUploadResult(null); }
              }}
              onClick={() => document.getElementById('paFileInput')?.click()}
            >
              <Upload className="w-12 h-12 text-slate-500 mx-auto mb-4" />
              <p className="text-slate-300 font-medium">
                {selectedFile ? selectedFile.name : 'Drag and drop a fund admin file, or click to browse'}
              </p>
              <p className="text-xs text-slate-500 mt-2">Accepts: .csv, .xlsx, .xls, .txt</p>
              <input id="paFileInput" type="file" className="hidden" accept=".csv,.xlsx,.xls,.txt"
                onChange={e => { setSelectedFile(e.target.files?.[0] || null); setUploadResult(null); }} />
            </div>

            {/* Upload result */}
            {uploadResult && (
              <div className={cn("mt-4 p-4 rounded-xl flex items-center gap-3", uploadResult.success ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-red-500/10 border border-red-500/20")}>
                {uploadResult.success ? <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                <div>
                  <p className={cn("font-medium", uploadResult.success ? "text-emerald-400" : "text-red-400")}>{uploadResult.message}</p>
                  {uploadResult.recordCount !== undefined && <p className="text-sm text-slate-400 mt-1">{uploadResult.recordCount} positions parsed</p>}
                </div>
              </div>
            )}

            {/* Form fields */}
            <div className="grid grid-cols-2 gap-6 mt-8">
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Fund Administrator</label>
                <select value={selectedAdmin} onChange={e => setSelectedAdmin(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none">
                  <option value="">Select Administrator...</option>
                  {FUND_ADMINS.map(a => <option key={a.code} value={a.code}>{a.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Statement Date</label>
                <DateField
                  value={fileDate ?? ''}
                  className="w-full"
                  onChange={iso => setFileDate(iso)}
                />
              </div>
            </div>

            <button onClick={handleUpload} disabled={isUploading || !selectedFile || !selectedAdmin || !fileDate}
              className="w-full mt-8 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20 flex items-center justify-center gap-2">
              {isUploading ? <RefreshCcw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Upload & Parse Statement
            </button>
          </div>
        </div>
      )}

      {/* ══════════════ FILES TAB ══════════════ */}
      {activeTab === 'files' && (
        <div className="space-y-6">
          {/* Recent uploads */}
          <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-white">Admin File Uploads</span>
              <span className="text-xs text-slate-500 ml-2">{files.length} files</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="py-3 px-4">ID</th>
                    <th className="py-3 px-4">File Name</th>
                    <th className="py-3 px-4">Administrator</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Records</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {files.map(f => (
                    <tr key={f.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 text-slate-400">#{f.id}</td>
                      <td className="py-3 px-4 font-medium text-white">{f.fileName}</td>
                      <td className="py-3 px-4 text-slate-300">{FUND_ADMINS.find(a => a.code === f.custodian)?.name || f.custodian}</td>
                      <td className="py-3 px-4 text-slate-300">{f.fileDate}</td>
                      <td className="py-3 px-4 text-slate-300">{f.recordCount}</td>
                      <td className="py-3 px-4">
                        <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold uppercase",
                          f.status === 'UPLOADED' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400')}>
                          {f.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {f.status === 'UPLOADED' ? (
                          <button onClick={() => handleRunReconciliation(f.id)}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all">
                            Run Reconciliation
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-500 uppercase">Completed</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {files.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-500 italic">No files uploaded yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Reconciliation runs */}
          <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-white/5 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold text-white">Reconciliation Runs</span>
              <span className="text-xs text-slate-500 ml-2">{runs.length} runs</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                    <th className="py-3 px-4">Run ID</th>
                    <th className="py-3 px-4">Time</th>
                    <th className="py-3 px-4">Admin Positions</th>
                    <th className="py-3 px-4">IBOR Positions</th>
                    <th className="py-3 px-4">Matched</th>
                    <th className="py-3 px-4">Breaks</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {runs.map(r => (
                    <tr key={r.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3 px-4 text-slate-400">#{r.id}</td>
                      <td className="py-3 px-4 text-slate-300">{new Date(r.runTime).toLocaleString()}</td>
                      <td className="py-3 px-4 text-slate-300">{r.totalCustodianPositions}</td>
                      <td className="py-3 px-4 text-slate-300">{r.totalOmsPositions}</td>
                      <td className="py-3 px-4 text-emerald-400 font-bold">{r.matchedCount}</td>
                      <td className="py-3 px-4 text-red-400 font-bold">{r.breakCount}</td>
                      <td className="py-3 px-4 text-right">
                        <button onClick={() => { setSelectedRunId(r.id); setActiveTab('breaks'); }}
                          className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-lg transition-all border border-white/5">
                          View Breaks
                        </button>
                      </td>
                    </tr>
                  ))}
                  {runs.length === 0 && (
                    <tr><td colSpan={7} className="py-12 text-center text-slate-500 italic">No reconciliation runs yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ BREAKS TAB ══════════════ */}
      {activeTab === 'breaks' && (
        <div className="bg-slate-900/40 border border-white/5 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              {selectedRunId ? `Breaks — Run #${selectedRunId}` : 'All Open Breaks'}
            </h3>
            <div className="flex items-center gap-3">
              {selectedRunId && (
                <button onClick={() => { setSelectedRunId(null); fetchBreaks(); }}
                  className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">
                  Show All Open
                </button>
              )}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Security</th>
                  <th className="py-3 px-4">Account</th>
                  <th className="py-3 px-4 text-right">Admin Value</th>
                  <th className="py-3 px-4 text-right">IBOR Value</th>
                  <th className="py-3 px-4 text-right">Difference</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Likely Cause</th>
                  <th className="py-3 px-4">Notes</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {breaks.map(b => (
                  <tr key={b.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-4">
                      <span className={cn("px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                        b.breakType === 'VALUE_MISMATCH' ? 'bg-red-500/10 text-red-400' :
                        b.breakType === 'QUANTITY_MISMATCH' ? 'bg-amber-500/10 text-amber-400' :
                        'bg-orange-500/10 text-orange-400')}>
                        {b.breakType?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-300 text-xs">{b.custodianCusip || b.omsCusip}</td>
                    <td className="py-3 px-4 text-xs text-slate-400">{b.custodianAccount || b.omsPortfolio}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-200">{b.custodianValue ? fmt(b.custodianValue) : '—'}</td>
                    <td className="py-3 px-4 text-right font-mono text-slate-200">{b.omsValue ? fmt(b.omsValue) : '—'}</td>
                    <td className="py-3 px-4 text-right font-mono font-bold">
                      {b.valueDiff != null ? (
                        <span className={Math.abs(b.valueDiff) < 0.01 ? 'text-emerald-400' : 'text-red-400'}>{fmtDelta(b.valueDiff)}</span>
                      ) : b.quantityDiff != null ? (
                        <span className="text-amber-400">{b.quantityDiff.toLocaleString()}</span>
                      ) : '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border", statusBadge(b.status))}>
                        {statusIcon(b.status)}{b.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 max-w-[280px]">
                      {b.inferredReason ? (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className={cn(
                              "px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider border",
                              b.inferredConfidence === 'HIGH' ? 'bg-teal-500/10 text-teal-300 border-teal-500/20' :
                              b.inferredConfidence === 'MED'  ? 'bg-amber-500/10 text-amber-300 border-amber-500/20' :
                                                                'bg-slate-500/10 text-slate-300 border-slate-500/20'
                            )}>{b.inferredConfidence || 'LOW'}</span>
                            {b.inferredAction && (
                              <span className="text-[10px] text-slate-400 font-semibold truncate">{b.inferredAction}</span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-300 leading-snug line-clamp-2" title={b.inferredReason}>{b.inferredReason}</div>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-500 max-w-[200px] truncate">{b.resolutionNotes || '—'}</td>
                    <td className="py-3 px-4 text-right">
                      {b.status !== 'RESOLVED' && (
                        <button onClick={() => setResolveModal({ isOpen: true, breakId: b.id })}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-all">
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {breaks.length === 0 && (
                  <tr><td colSpan={10} className="py-12 text-center text-slate-500 italic">No breaks found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ RESOLVE MODAL ══════════════ */}
      <AnimatePresence>
        {resolveModal.isOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={() => setResolveModal({ isOpen: false, breakId: null })} />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-slate-900 border border-white/10 rounded-3xl shadow-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-6">Resolve Break #{resolveModal.breakId}</h3>
              <div className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Resolution Status</label>
                  <select value={resolveStatus} onChange={e => setResolveStatus(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-blue-500/50 appearance-none">
                    <option value="RESOLVED">Resolved (Positions Fixed)</option>
                    <option value="ACCEPTED">Accepted (Valid Timing Difference)</option>
                    <option value="INVESTIGATING">Still Investigating</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Resolution Notes</label>
                  <textarea value={resolveNotes} onChange={e => setResolveNotes(e.target.value)}
                    className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 min-h-[120px]"
                    placeholder="Describe how this break was resolved (e.g., PIK accrual posted, admin confirmed paydown)..." />
                </div>
                <div className="flex gap-4 pt-4">
                  <button onClick={() => setResolveModal({ isOpen: false, breakId: null })}
                    className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-xl transition-all">Cancel</button>
                  <button onClick={handleResolveBreak}
                    className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-600/20">Save Resolution</button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PrivateAssetReconciliationScreen;
